import { Button, Textarea, useKumoToastManager } from '@cloudflare/kumo';
import { Select } from '@cloudflare/kumo/components/select';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { conditionalJsonPatch } from '../../shared/json-patch.ts';
import { api, invalidateSite, meQuery, queryClient, siteSettingsQuery, sitesQuery, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { Route as parent } from './site.tsx';

export const Route = createRoute({
  getParentRoute: () => parent,
  path: 'blog-automation',
  remountDeps: ({ params }) => params.siteId,
  component: BlogAutomation,
});

type Instructions = { businessOverview: string; instructions: string; postCollection: string };
const pendingStatuses = ['Pending', 'Processing', 'AwaitingPaymentMethod', 'AwaitingPayment'];
const servicePaths = {
  Activate: '/api/sites/{siteId}/blog-automation/service',
  Cancel: '/api/sites/{siteId}/blog-automation/service/cancel',
  Restore: '/api/sites/{siteId}/blog-automation/service/restore',
} as const;
const date = (value: string) => new Date(value).toLocaleDateString(undefined, { dateStyle: 'long' });

function BlogAutomation() {
  const { siteId } = Route.useParams();
  const me = useQuery(meQuery);
  const sites = useQuery(sitesQuery);
  const website = useQuery(siteSettingsQuery(siteId));
  const metadata = useQuery({
    queryKey: ['site', siteId, 'metadata'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/sites/{siteId}', {
          params: { path: { siteId } },
        }),
      ),
  });
  const site = metadata.data;
  const summary = sites.data?.find((entry) => entry.id === siteId);
  const publishedWebsite = useQuery({
    ...siteSettingsQuery(siteId, 'Published'),
    enabled: Boolean(summary?.published),
  });
  const owner = Boolean(site && me.data?.id === site.ownerUserId);
  const accountId = site?.ownerUserId ?? '';
  const toast = useKumoToastManager();
  const [edits, setEdits] = useState<Partial<Instructions>>({});
  const billing = useQuery({
    queryKey: ['billing', accountId],
    enabled: owner,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/accounts/{accountUserId}/billing', {
          params: { path: { accountUserId: accountId } },
        }),
      ),
    refetchInterval: (query) =>
      query.state.data?.operations.some(
        (operation) => operation.siteId === siteId && pendingStatuses.includes(operation.status),
      )
        ? 2000
        : false,
  });
  const pending = billing.data?.operations.find(
    (operation) => operation.siteId === siteId && pendingStatuses.includes(operation.status),
  );
  const settings = useQuery({
    queryKey: ['blog-automation', siteId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/sites/{siteId}/blog-automation', {
          params: { path: { siteId } },
        }),
      ),
    refetchInterval: (query) => (pending ? 2000 : query.state.data?.enabled ? 10_000 : false),
  });
  // Billing completion must refresh service status even when the settings poll was inactive.
  useEffect(() => {
    if (billing.dataUpdatedAt) void queryClient.invalidateQueries({ queryKey: ['blog-automation', siteId] });
  }, [billing.dataUpdatedAt, siteId]);
  useEffect(() => {
    if (settings.dataUpdatedAt)
      void queryClient.invalidateQueries({ queryKey: ['site', siteId, 'metadata'] });
  }, [settings.dataUpdatedAt, siteId]);
  const save = useMutation({
    mutationFn: async (instructions: Instructions) =>
      unwrap(
        await api.PATCH('/api/sites/{siteId}/settings', {
          params: { path: { siteId } },
          headers: { 'Content-Type': 'application/json-patch+json' },
          body: conditionalJsonPatch(
            website.data!,
            [
              { path: ['businessOverview'], value: instructions.businessOverview },
              { path: ['blogAutomationInstructions'], value: instructions.instructions },
              { path: ['blogAutomationPostCollection'], value: instructions.postCollection },
            ],
            website.data!.versionId,
          ),
        }),
      ),
    onSuccess: async () => {
      await invalidateSite(siteId);
      await queryClient.invalidateQueries({ queryKey: ['blog-automation', siteId] });
      setEdits({});
      toast.add({ title: 'Blog instructions saved.', variant: 'success' });
    },
  });
  const change = useMutation({
    mutationFn: async (kind: keyof typeof servicePaths) => {
      if (kind === 'Activate' && (changed || !draftConfigured)) await save.mutateAsync(instructions);
      return unwrap(
        await api.POST(servicePaths[kind], {
          params: { path: { siteId } },
          body: { idempotencyKey: crypto.randomUUID() },
        }),
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['billing', accountId] });
      await queryClient.invalidateQueries({ queryKey: ['blog-automation', siteId] });
    },
  });
  if (
    settings.isPending ||
    website.isPending ||
    metadata.isPending ||
    sites.isPending ||
    me.isPending ||
    publishedWebsite.isLoading
  )
    return <Loading />;
  if (settings.error || website.error || metadata.error || sites.error || me.error || publishedWebsite.error)
    return (
      <ErrorNotice
        error={
          settings.error ??
          website.error ??
          metadata.error ??
          sites.error ??
          me.error ??
          publishedWebsite.error
        }
      />
    );
  const data = settings.data!;
  const collections = website.data!.postCollections;
  const initial: Instructions = {
    businessOverview: website.data!.businessOverview ?? '',
    instructions: website.data!.blogAutomationInstructions ?? '',
    postCollection:
      website.data!.blogAutomationPostCollection ?? (collections.length === 1 ? collections[0]! : ''),
  };
  const instructions = { ...initial, ...edits };
  const changed = Object.entries(instructions).some(
    ([key, value]) => value !== initial[key as keyof Instructions],
  );
  const draftConfigured = Boolean(
    website.data!.businessOverview && website.data!.blogAutomationPostCollection,
  );
  const monthlyPrice = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: data.monthlyPriceCents % 100 === 0 ? 0 : 2,
  }).format(data.monthlyPriceCents / 100);
  const valid = !site?.archivedAt;
  const covered =
    data.enabled &&
    (!data.cancellationAt || new Date(data.cancellationAt).getTime() > settings.dataUpdatedAt);
  const publishedSettings = publishedWebsite.data;
  const publishedConfigured = Boolean(
    publishedSettings?.businessOverview?.trim() &&
    publishedSettings.blogAutomationPostCollection &&
    publishedSettings.postCollections.includes(publishedSettings.blogAutomationPostCollection),
  );
  const generationAvailable =
    covered && !site?.archivedAt && publishedConfigured && data.baseServiceAvailable;
  const base = billing.data?.sites.find((entry) => entry.id === siteId);
  const eligible = Boolean(
    !site?.archivedAt &&
    data.baseServiceAvailable &&
    base &&
    !base.cancellationAt &&
    ['Trialing', 'Active', 'PastDue'].includes(billing.data?.stripeSubscriptionStatus ?? ''),
  );
  const busy = save.isPending || change.isPending;
  const paymentNeeded =
    pending?.service === 'BlogAutomation' &&
    ['AwaitingPayment', 'AwaitingPaymentMethod'].includes(pending.status);
  return (
    <section className="max-w-212.5 pt-7 pb-12.5 min-[761px]:pt-[35px] min-[761px]:pb-17.5 grid gap-7">
      <div>
        <h2 className="text-[20px] font-semibold tracking-[-0.35px]">Blog automation</h2>
        <p className="mt-2 text-kumo-subtle text-sm leading-relaxed">
          A researched article with a generated featured image, published automatically every week.{' '}
          {monthlyPrice}/month for this website, with up to five published articles per billing period.
        </p>
      </div>
      <ErrorNotice error={save.error ?? change.error ?? billing.error} />
      <div className="rounded-xl border border-kumo-line bg-kumo-base p-5 grid gap-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold">
              {covered
                ? !generationAvailable
                  ? 'Generation unavailable'
                  : data.cancellationAt
                    ? 'Renewal canceled'
                    : 'Active'
                : 'Not active'}
            </h3>
            {covered && data.cancellationAt ? (
              <p className="mt-1 text-sm text-kumo-subtle">Service ends {date(data.cancellationAt)}.</p>
            ) : null}
          </div>
          {owner && covered && !pending ? (
            <Button
              disabled={busy || (Boolean(data.cancellationAt) && !eligible)}
              onClick={() => change.mutate(data.cancellationAt ? 'Restore' : 'Cancel')}
            >
              {data.cancellationAt ? 'Restore renewal' : 'Cancel renewal'}
            </Button>
          ) : null}
        </div>
        {data.allowance ? (
          <p className="text-sm">{data.allowance.published} of 5 articles published this billing period.</p>
        ) : covered ? (
          <p className="text-sm text-kumo-subtle">Billing details are updating.</p>
        ) : null}
        {generationAvailable &&
        data.nextGenerationAt &&
        (!data.cancellationAt || data.nextGenerationAt < data.cancellationAt) ? (
          <p className="text-sm text-kumo-subtle">
            {data.allowance?.available === 0
              ? 'This period’s article limit has been reached.'
              : new Date(data.nextGenerationAt).getTime() <= settings.dataUpdatedAt
                ? 'The next article will start soon.'
                : `Next article starts ${new Date(data.nextGenerationAt).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })}.`}
          </p>
        ) : null}
        {!owner ? (
          <p className="text-sm text-kumo-subtle">Only the website owner can manage this add-on’s billing.</p>
        ) : billing.isPending ? (
          <Loading label="Loading billing…" />
        ) : billing.data ? (
          <>
            {site?.archivedAt ? (
              <p className="text-sm text-kumo-subtle">
                This website is archived. Blog generation is unavailable.
              </p>
            ) : !eligible ? (
              <p className="text-sm text-kumo-subtle">
                Manage the website’s base plan in{' '}
                <Link className="underline" to="/billing" search={{ account: accountId }}>
                  Billing
                </Link>{' '}
                to use blog automation.
              </p>
            ) : null}
            {pending ? (
              <div className="grid gap-2 text-sm" role="status">
                <p>
                  {paymentNeeded
                    ? 'Complete payment to activate blog automation.'
                    : 'Your billing change is processing…'}
                </p>
                {pending.error ? <p className="text-kumo-subtle">{pending.error}</p> : null}
                <Link className="underline" to="/billing" search={{ account: accountId }}>
                  Continue in Billing
                </Link>
              </div>
            ) : !covered ? (
              <>
                {billing.data.stripeSubscriptionStatus === 'Trialing' && billing.data.trialEndsAt ? (
                  <p className="text-sm text-kumo-subtle">
                    Included for the rest of your trial through {date(billing.data.trialEndsAt)}, then{' '}
                    {monthlyPrice}/month.
                  </p>
                ) : billing.data.periodEndsAt ? (
                  <p className="text-sm text-kumo-subtle">
                    Your first charge is prorated through {date(billing.data.periodEndsAt)}. Then{' '}
                    {monthlyPrice}/month on your existing subscription.
                  </p>
                ) : null}
                {eligible && !billing.data.paymentMethod ? (
                  <Link className="underline text-sm" to="/billing" search={{ account: accountId }}>
                    Add a payment method in Billing
                  </Link>
                ) : null}
                <Button
                  variant="primary"
                  className="justify-self-start"
                  disabled={busy || !valid || !eligible || !billing.data.paymentMethod}
                  onClick={() => change.mutate('Activate')}
                >
                  {change.isPending
                    ? 'Activating…'
                    : changed || !draftConfigured
                      ? `Save and activate · ${monthlyPrice}/month`
                      : `Activate · ${monthlyPrice}/month`}
                </Button>
              </>
            ) : null}
            <Link
              className="underline text-sm justify-self-start"
              to="/billing"
              search={{ account: accountId }}
            >
              Manage payment method and invoices
            </Link>
          </>
        ) : null}
      </div>
      <form
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid && !busy) save.mutate(instructions);
        }}
      >
        <fieldset disabled={busy} className="grid gap-5 min-w-0">
          <div>
            <Textarea
              label="Business brief"
              rows={6}
              maxLength={30_000}
              value={instructions.businessOverview}
              onChange={(event) => setEdits({ ...edits, businessOverview: event.target.value })}
            />
            <p className="mt-2 text-sm text-kumo-subtle">
              Confirm the business facts the writer can rely on, including your services, customers, and areas
              of expertise.
            </p>
          </div>
          <div>
            <Textarea
              label="Writing instructions"
              rows={6}
              maxLength={30_000}
              value={instructions.instructions}
              onChange={(event) => setEdits({ ...edits, instructions: event.target.value })}
            />
            <p className="mt-2 text-sm text-kumo-subtle">
              Describe your voice, topics to avoid, image preferences, and other ongoing guidance.
            </p>
          </div>
          <Select<string>
            label="Blog collection"
            className="w-full"
            value={instructions.postCollection}
            items={Object.fromEntries(collections.map((collection) => [collection, collection]))}
            onValueChange={(value) => {
              if (value !== null) setEdits({ ...edits, postCollection: value });
            }}
          />
          {!site?.archivedAt && !summary?.published ? (
            <p className="text-sm text-kumo-subtle">
              Create and publish a blog page in the website’s primary language before setting up automation.{' '}
              <Link className="underline" to="/sites/$siteId/pages" params={{ siteId }}>
                Open pages
              </Link>
            </p>
          ) : null}
        </fieldset>
        <Button
          type="submit"
          className="justify-self-start"
          disabled={busy || !valid || (!changed && draftConfigured)}
        >
          {save.isPending ? 'Saving…' : 'Save instructions'}
        </Button>
      </form>
    </section>
  );
}
